import { Renderer } from '../renderer/Renderer';
import { Timeline, Framerate } from './models/Timeline';
import { Element } from './models/Element';
import { Track } from './models/Track';
import {
  Command,
  TimelineMode,
  ShiftElementCommand,
  TrimElementStartCommand,
  TrimElementEndCommand,
  RemoveTrackCommand,
  CreateTrackCommand,
  SplitElementCommand,
  TrackLayerUpCommand,
  TrackLayerDownCommand,
  TranscriptSimpleRestoreCommand,
  TranscriptSimpleInsertCommand,
  TranscriptSimpleRemoveCommand,
} from './commands';
import { Transcript } from './models/Transcript';
import { VideoElement } from './models/elements/VideoElement';
import { TranscriptElement } from '../types.ts/transcript';
import { RendererSynchronizer } from './RendererSynchronizer';
import { CommandManager } from './lib/CommandManager';
import { ObserverManager } from './lib/ObserverManager';
import { EditorObserver, EditorChangeType } from './types';
import { TrackManager } from './lib/TrackManager';
import { AddElementCommand } from './commands/AddElementCommand';
import { AddMediaElementCommand } from './commands/AddMediaElementCommand';
import {
  AddMusicElementCommand,
  AddMusicElementOptions,
} from './commands/AddMusicElementCommand';
import { AddTextElementCommand } from './commands/AddTextElementCommand';
import {
  AddVideoElementCommand,
  AddVideoElementOptions,
} from './commands/AddVideoElementCommand';
import { UpdateElementCommand } from './commands/UpdateElementCommand';
import { RemoveElementCommand } from './commands/RemoveElementCommand';
import { MoveElementToTrackCommand } from './commands/MoveElementToTrackCommand';
import { VideoEditorLogger } from '../utility/VideoEditorLogger';
import { AddLayoutElementsCommand } from './commands/AddLayoutElementsCommand';
import { ClearLayoutElementsCommand } from './commands/ClearLayoutElementsCommand';
import { Video } from '@src/types.ts/story';
import {
  ResultElements as SplitResultElements,
  SplitLayoutElementsCommand,
} from './commands/SplitLayoutElementsCommand';
import { DeleteLayoutElementsCommand } from './commands/DeleteLayoutElementsCommand';
import { TrimLayoutElementsCommand } from './commands/TrimLayoutElementsCommand';
import {
  ExciseLayoutElementsCommand,
  ResultElements as ExciseResultElements,
} from './commands/ExciseLayoutElementsCommand';
import MergeLayoutElementsCommand from './commands/MergeLayoutElementsCommand';
import {
  ResultElements as SwitchResultElements,
  SwitchLayoutTypeCommand,
} from './commands/SwitchLayoutTypeCommand';

export interface CutTranscriptSegment {
  elementId: string;
  initialIndexes: number[];
}

export interface PasteTranscriptSegment {
  intoTime: number;
  startInitialIndex: number;
  endInitialIndex: number;
}

export class VideoEditor {
  // Core state
  timeline: Timeline;
  transcript?: Transcript;
  private rendererSynchronizer: RendererSynchronizer;
  private commandManager: CommandManager;
  private trackManager: TrackManager;
  private transcriptTrackId?: string; // New property to store ID of track with transcript

  /**
   * Optional listener that will be called every time a command is successfully executed.
   * The listener can be used by host applications (e.g. VideoCreatorStore) to hook into
   * the editor's internal command execution and maintain a consolidated undo/redo stack.
   */
  public commandExecutedListener?: (command: Command) => void;

  // State management
  private batchDepth: number = 0; // Counter for backward compatibility
  private inBatch: boolean = false; // Simpler flag for batch operations
  private dirty: boolean = false; // Flag to track if timeline needs syncing
  private _isSyncingToRenderer: boolean = false; // For compatibility with existing code

  // Cut Paste
  private cutInitialIndexes: number[] = [];

  // Timeline editing mode
  private timelineMode: TimelineMode = TimelineMode.DEFAULT;

  // Observer pattern implementation (moved to ObserverManager)
  private observerManager: ObserverManager;

  // Legacy event handler (kept for backward compatibility)
  onStateChange?: (timeline: Timeline) => void;

  constructor() {
    this.timeline = new Timeline();
    this.rendererSynchronizer = new RendererSynchronizer(this);
    this.commandManager = new CommandManager(this);
    this.observerManager = new ObserverManager(this);

    // Create TrackManager with this instance as the editor
    this.trackManager = new TrackManager(this);

    // Initialize transcript with an empty array to avoid null references
    this.transcript = new Transcript([]);

    // Initialize editor logging
    this.initializeLogging();
  }

  /**
   * Initialize editor logging settings
   * This can be called again to change logging configuration
   */
  initializeLogging(
    options: {
      enableCommandLogging?: boolean;
      enableTimelineLogging?: boolean;
      enableTranscriptLogging?: boolean;
      enableBatchLogging?: boolean;
      enablePerformanceLogging?: boolean;
      enableStateLogging?: boolean;
    } = {},
  ): void {
    // Check if we're in development environment
    const isDev = process.env.NODE_ENV === 'development';

    VideoEditorLogger.initialize({
      // In dev mode, enable most channels by default unless explicitly disabled
      commandsEnabled: options.enableCommandLogging ?? true,
      timelineEnabled: options.enableTimelineLogging ?? isDev,
      transcriptEnabled: options.enableTranscriptLogging ?? isDev,
      batchingEnabled: options.enableBatchLogging ?? isDev,
      performanceEnabled: options.enablePerformanceLogging ?? isDev,
      stateEnabled: options.enableStateLogging ?? isDev,
    });

    VideoEditorLogger.logStateChange('editor_initialized', {
      timelineId: this.timeline.id,
      hasTranscript: !!this.transcript,
      environment: process.env.NODE_ENV,
      isDev,
    });
  }

  /**
   * Gets the track manager instance
   * @returns The TrackManager instance
   */
  getTrackManager(): TrackManager {
    return this.trackManager;
  }

  /**
   * Gets the current timeline mode
   */
  getTimelineMode(): TimelineMode {
    return this.timelineMode;
  }

  /**
   * Sets the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    this.timelineMode = mode;
  }

  /**
   * Gets the current framerate
   */
  getFramerate(): Framerate {
    return this.timeline.fps;
  }

  /**
   * Sets the framerate
   */
  setFramerate(fps: Framerate): void {
    this.timeline.setFps(fps);
    this.markDirty('framerate_change');

    // Notify observers about the framerate change
    this.notifyObservers(EditorChangeType.FRAMERATE_CHANGED, {
      fps: fps,
      previousFps: this.timeline.fps,
    });
    // Also notify of general timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'framerate_change',
      fps: fps,
    });

    this.maybeSync();
  }

  /**
   * Gets the current snap-to-frame setting
   */
  getSnapToFrame(): boolean {
    return this.timeline.snapToFrame;
  }

  /**
   * Sets the snap-to-frame setting
   */
  setSnapToFrame(snapToFrame: boolean): void {
    const previousValue = this.timeline.snapToFrame;
    this.timeline.setSnapToFrame(snapToFrame);
    this.markDirty('snap_to_frame_change');

    // Notify observers about the snap setting change
    this.notifyObservers(EditorChangeType.SNAP_TO_FRAME_CHANGED, {
      snapToFrame: snapToFrame,
      previousValue: previousValue,
    });
    // Also notify of general timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'snap_to_frame_change',
      snapToFrame: snapToFrame,
    });

    this.maybeSync();
  }

  /**
   * Converts time in seconds to frame number based on the current framerate
   */
  timeToFrame(time: number): number {
    return this.timeline.timeToFrame(time);
  }

  /**
   * Converts frame number to time in seconds based on the current framerate
   */
  frameToTime(frame: number): number {
    return this.timeline.frameToTime(frame);
  }

  /**
   * Snaps time to the nearest frame boundary if snap-to-frame is enabled
   */
  snapTimeToFrame(time: number): number {
    return this.timeline.snapTimeToFrame(time);
  }

  /**
   * Executes a command and adds it to history
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  executeCommand(command: Command): boolean {
    return this.executeInBatch(() => {
      const startTime = performance.now();
      const result = this.commandManager.execute(command);
      const endTime = performance.now();

      // Log the execution result
      if (result) {
        VideoEditorLogger.logCommand(command, 'complete', {
          durationMs: endTime - startTime,
        });

        // Notify external listener (e.g., host application) that a command has been executed
        if (this.commandExecutedListener) {
          try {
            this.commandExecutedListener(command);
          } catch (err) {
            // Listener errors should not break editor functionality
            console.error('commandExecutedListener threw an error', err);
          }
        }
      } else {
        VideoEditorLogger.logCommand(command, 'error');
      }

      return result;
    });
  }

  /**
   * Undoes the last command
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  undo(): boolean {
    console.warn(
      'Undo from headless not ready yet, videoCreator.undo() should be used instead',
    );
    const command = this.commandManager.getLastExecutedCommand();
    if (!command) {
      VideoEditorLogger.logStateChange('undo', { error: 'No command to undo' });
      return false;
    }

    return this.executeInBatch(() => {
      const result = this.commandManager.undo();
      VideoEditorLogger.logCommandUndo(command, result);
      return result;
    });
  }

  /**
   * Redoes a previously undone command
   * Delegates to the CommandManager
   * Uses executeInBatch to ensure proper batching
   */
  redo(): boolean {
    console.warn(
      'Redo from headless not ready yet, videoCreator.redo() should be used instead',
    );
    const command = this.commandManager.getNextUndoneCommand();
    if (!command) {
      VideoEditorLogger.logStateChange('redo', { error: 'No command to redo' });
      return false;
    }

    return this.executeInBatch(() => {
      const result = this.commandManager.redo();
      VideoEditorLogger.logCommandRedo(command, result);
      return result;
    });
  }

  /**
   * Clears the internal undo/redo history maintained by the CommandManager.
   * This is used by host applications (e.g. legacy VideoCreatorStore) when
   * they revert the full video state via snapshots.  After such a revert the
   * existing command history inside the headless editor is no longer valid,
   * so we wipe it to avoid allowing the user to redo commands that no longer
   * apply to the current timeline.
   */
  clearHistory(): void {
    this.commandManager.clearHistory();
  }

  /**
   * Initializes and attaches all built-in observers
   * Call this method to automatically set up the default observers
   */
  initializeBuiltInObservers(): void {
    // Create and register a renderer sync observer
    const rendererSyncObserver = {
      onEditorChange: (
        editor: VideoEditor,
        changeType: EditorChangeType,
        payload: any,
      ) => {
        // Sync to renderer for timeline changes (unless we're coming from the renderer)
        if (
          changeType === EditorChangeType.TIMELINE_CHANGED &&
          payload.source !== 'renderer_sync'
        ) {
          VideoEditorLogger.logStateChange('sync_to_renderer', {
            triggerChangeType: changeType,
            payload,
          });
          this.rendererSynchronizer.syncToRenderer();
        }
      },
    };
    this.registerObserver(rendererSyncObserver);

    // Create and register a transcript refresh observer
    const transcriptRefreshObserver = this.createTranscriptRefreshObserver();
    this.registerObserver(transcriptRefreshObserver);
  }

  /**
   * Registers an observer for specific change types
   * Delegates to ObserverManager
   * @param observer The observer to register
   * @param changeTypes The types of changes to observe (if none provided, observes all changes)
   */
  registerObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    this.observerManager.registerObserver(observer, changeTypes);
  }

  /**
   * Unregisters an observer from specific change types
   * Delegates to ObserverManager
   * @param observer The observer to unregister
   * @param changeTypes The types of changes to unobserve (if none provided, unobserves all changes)
   */
  unregisterObserver(
    observer: EditorObserver,
    changeTypes?: EditorChangeType[],
  ): void {
    this.observerManager.unregisterObserver(observer, changeTypes);
  }

  /**
   * Notifies all registered observers about a change
   * Delegates to ObserverManager
   * @param changeType The type of change that occurred
   * @param payload Optional data associated with the change
   * @param immediate Optional flag to force immediate notification even during batching (use with caution)
   */
  notifyObservers(
    changeType: EditorChangeType,
    payload?: any,
    immediate: boolean = false,
  ): void {
    this.observerManager.notifyObservers(changeType, payload, immediate);

    // If this is a timeline-related change but not the general TIMELINE_CHANGED event,
    // also emit a TIMELINE_CHANGED event to ensure MobX reactivity is triggered
    if (
      changeType !== EditorChangeType.TIMELINE_CHANGED &&
      [
        EditorChangeType.ELEMENT_ADDED,
        EditorChangeType.ELEMENT_REMOVED,
        EditorChangeType.ELEMENT_UPDATED,
        EditorChangeType.ELEMENT_SHIFTED,
        EditorChangeType.ELEMENT_TRACK_CHANGED,
        EditorChangeType.ELEMENT_TRIMMED_START,
        EditorChangeType.ELEMENT_TRIMMED_END,
        EditorChangeType.TRACK_ADDED,
        EditorChangeType.TRACK_REMOVED,
      ].includes(changeType)
    ) {
      this.observerManager.notifyObservers(
        EditorChangeType.TIMELINE_CHANGED,
        {
          source: changeType,
          details: payload,
        },
        immediate,
      );
    }
  }

  /**
   * Checks if sync is needed and performs it if necessary
   * Only syncs if not in a batch operation and editor is dirty
   * Also processes any queued notifications after syncing
   */
  private maybeSync(): void {
    if (!this.isInBatch() && this.dirty) {
      this.rendererSynchronizer.syncToRenderer();
      this.dirty = false;

      // Process any pending notifications after syncing
      this.observerManager.processQueuedNotifications();
    }
  }

  /**
   * Returns the current batch depth
   * Exposed for CommandManager to use
   */
  getBatchDepth(): number {
    return this.batchDepth;
  }

  /**
   * Returns whether we're currently in a batch operation
   * Replacement for getBatchDepth() > 0 checks
   */
  isInBatch(): boolean {
    return this.batchDepth > 0;
  }

  /**
   * Executes an operation within a batch, ensuring proper begin/end batch handling
   * This is the preferred way to execute operations that need to be batched
   * @param operation The function to execute within the batch
   * @returns The result of the operation
   */
  executeInBatch<T>(operation: () => T): T {
    const wasTopLevelBatch = this.batchDepth === 0;

    // Start the batch if this is a top-level call
    if (wasTopLevelBatch) {
      this.beginBatch();
    }

    try {
      // Execute the operation
      return operation();
    } finally {
      // End the batch if we started it
      if (wasTopLevelBatch) {
        this.endBatch();
      }
    }
  }

  /**
   * Marks the timeline as dirty and requiring sync
   * Centralized method to ensure consistent behavior
   * Made public for CommandManager to use
   * @param source Descriptive source of the change for logging
   */
  markDirty(source: string = 'unknown'): void {
    if (!this.dirty) {
      // Replace console log with structured logging
      VideoEditorLogger.logStateChange('timeline_dirty', { source });

      this.dirty = true;
      this.rendererSynchronizer.markDirty(source);
    }
  }

  /**
   * Connects the editor to a Renderer instance
   */
  connectRenderer(renderer: Renderer): void {
    this.rendererSynchronizer.connectRenderer(renderer);
  }

  /**
   * Force a sync from the renderer state
   * This can be called externally to ensure state is up-to-date
   * @param rendererState The state from the renderer
   */
  syncFromRenderer(rendererState: any, newTimeline?: Timeline): void {
    if (!rendererState) {
      VideoEditorLogger.logStateChange('sync_from_renderer_error', {
        error: 'No renderer state provided',
      });
      return;
    }

    // Update our timeline reference with the new timeline if provided
    if (newTimeline) {
      this.timeline = newTimeline;
    } else {
      // Fallback to creating a new timeline if one wasn't provided
      this.timeline = Timeline.fromJSON(rendererState);
    }

    // Notify observers about the timeline change
    this.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
      source: 'renderer_sync',
    });

    // Legacy callback support
    if (this.onStateChange) {
      this.onStateChange(this.timeline);
    }
  }

  /**
   * Loads a project from JSON data
   */
  loadProject(json: Record<string, any>): void {
    this.timeline = Timeline.fromJSON(json);

    // For now assume first video track is the transcript track
    const firstVideoTrack = this.timeline.tracks.find(
      (track) => track.type === 'video',
    );

    if (firstVideoTrack) {
      this.setTranscriptTrack(firstVideoTrack?.id);
    }

    this.dirty = true;

    // Notify observers
    this.notifyObservers(EditorChangeType.PROJECT_LOADED, {
      timeline: this.timeline,
      source: json,
    });

    this.maybeSync();
  }

  /**
   * Creates a new track and adds it to the timeline
   * @param type The type of track to create
   * @returns The newly created track
   */
  commandCreateTrack(
    type: 'video' | 'audio' | 'text' | 'image' = 'video',
  ): Track {
    // Use command pattern for track creation
    const command = new CreateTrackCommand(this, type);
    this.executeCommand(command);

    // The command stores the created track, so we can access it
    return command.createdTrack!;
  }

  /**
   * Removes a track from the timeline
   * @param trackId The track or track ID to remove
   * @returns true if removed, false otherwise
   */
  commandRemoveTrack(trackId: Track | string): boolean {
    // Use command pattern for track removal
    return this.executeCommand(new RemoveTrackCommand(this, trackId));
  }

  /**
   * Adds an element to a track
   * @param trackId The ID of the track to add the element to
   * @param element The element to add
   * @param sync Whether to sync to the renderer immediately (default: true)
   */
  commandAddElementToTrack(trackId: string, element: Element): boolean {
    // Use command pattern for adding elements
    return this.executeCommand(new AddElementCommand(this, trackId, element));
  }

  /**
   * Creates a new element and adds it to a track
   */
  commandCreateAndAddElement(
    trackId: string,
    type: string,
    props: Record<string, any> = {},
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      props.time = this.timeline.snapTimeToFrame(props.time);
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    const element = Timeline.createElement(type, props);

    // Use command pattern for adding the element
    const success = this.executeCommand(
      new AddElementCommand(this, trackId, element),
    );

    return success ? element : null;
  }

  /**
   * Updates an element's properties
   * @param elementId The ID of the element to update
   * @param key The property key to update
   * @param value The new value for the property
   * @returns true if successful, false otherwise
   */
  commandUpdateElementProperty(
    elementId: string,
    key: string,
    value: any,
  ): boolean {
    const props: Record<string, any> = {};
    props[key] = value;

    // Use UpdateElementCommand for property updates
    return this.executeCommand(
      new UpdateElementCommand(this, elementId, props),
    );
  }

  /**
   * Updates multiple element properties at once
   * Can either accept a partial Element object or a state record from getState()
   */
  commandUpdateElement(elementId: string, props: Record<string, any>): boolean {
    // Use factory method to handle potential type transitions
    return this.executeCommand(
      UpdateElementCommand.create(this, elementId, props),
    );
  }

  /**
   * Removes an element from the timeline
   */
  commandRemoveElement(elementId: string): boolean {
    // Use RemoveElementCommand for element removal
    return this.executeCommand(new RemoveElementCommand(this, elementId));
  }

  /**
   * Moves an element to a different track
   */
  commandMoveElementToTrack(elementId: string, targetTrackId: string): boolean {
    // Use MoveElementToTrackCommand for moving elements between tracks
    return this.executeCommand(
      new MoveElementToTrackCommand(this, elementId, targetTrackId),
    );
  }

  /**
   * Adds a media element to the timeline with collision detection and automatic track management.
   * Supports TimelineMode for different collision handling behaviors:
   * - DEFAULT: Creates new track on collision
   * - RIPPLE: Shifts subsequent elements to make room
   * - OVERLAP: Allows overlapping elements
   *
   * @param element The media element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns AddMediaElementCommand instance if successful, null otherwise
   */
  commandAddMediaElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
  ): AddMediaElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddMediaElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Adds a music/audio element to the timeline with music-specific features:
   * - Fit to end: automatically adjusts duration to fill to timeline end
   * - Volume control: sets initial volume level
   * - Auto fade: applies fade in/out effects
   * - Smart track placement: fills gaps in existing music tracks before creating new ones
   *
   * @param element The music/audio element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param options Music-specific options (fitToEnd, volume, autoFade)
   * @returns AddMusicElementCommand instance if successful, null otherwise
   */
  commandAddMusicElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    options?: AddMusicElementOptions,
  ): AddMusicElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddMusicElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      options,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Creates and adds a text element to the timeline with smart positioning and validation
   * @param props Text element properties and configuration
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @returns The created Element if successful, null otherwise
   */
  commandCreateTextElement(
    props: Record<string, any> = {},
    targetTrackId?: string,
    mode?: TimelineMode,
  ): Element | null {
    // Apply frame snapping to time and duration values if provided
    if (props.time !== undefined) {
      props.time = this.timeline.snapTimeToFrame(props.time);
    }

    if (props.duration !== undefined && typeof props.duration === 'number') {
      props.duration = this.timeline.snapTimeToFrame(props.duration);
    }

    // Set default text properties if not provided
    const textProps = {
      type: 'text',
      duration: 4,
      font_size: '32',
      font_family: 'Inter',
      font_weight: '600',
      fill_color: 'rgba(0, 0, 0, 1)',
      background_color: 'rgba(255, 255, 255, 0.7)',
      background_align_threshold: '0%',
      background_border_radius: '0%',
      width: '76%',
      y: '60%',
      animations: [
        { type: 'fade', time: 0, duration: 1 },
        { type: 'fade', time: 'end', duration: 1, reversed: true },
      ],
      ...props,
    };

    const element = Timeline.createElement('text', textProps);

    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddTextElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
    );

    const success = this.executeCommand(command);
    return success ? element : null;
  }

  /**
   * Adds a video element to the timeline with video-specific features:
   * - Volume control: sets initial volume level
   * - Video fit: controls how video is resized to fit the element
   * - Loop control: enables/disables video looping
   * - Auto fade: applies fade in/out effects to video audio
   * - Smart track placement: prefers video tracks and handles collision detection
   *
   * @param element The video element to add
   * @param targetTrackId Optional specific track ID to add to
   * @param mode Timeline mode for collision handling (defaults to current editor mode)
   * @param options Video-specific options (volume, fit, loop, autoFade)
   * @returns AddVideoElementCommand instance if successful, null otherwise
   */
  commandAddVideoElement(
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    options?: AddVideoElementOptions,
  ): AddVideoElementCommand | null {
    const effectiveMode = mode ?? this.getTimelineMode();
    const command = new AddVideoElementCommand(
      this,
      element,
      targetTrackId,
      effectiveMode,
      options,
    );

    const success = this.executeCommand(command);
    return success ? command : null;
  }

  /**
   * Puts element into a stacked layout view
   */
  commandAddLayoutElements(
    elementId: string,
    layoutType: 'stacked-1-1' | 'stacked-1-3',
  ): [Element, Element] | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new AddLayoutElementsCommand(this, element, {
      layoutType,
    });

    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Removes element from a stacked layout view
   */
  commandClearLayoutElements(
    elementId: string,
    fit: 'cover' | 'contain' | 'fill' = 'cover',
    videoSourceWidthPrev?: number,
    videoSourceWidthCurr?: number,
  ): boolean {
    const element = this.getElementById(elementId);
    if (!element) {
      return false;
    }

    const command = new ClearLayoutElementsCommand(this, element, {
      fit,
      videoSourceWidthPrev,
      videoSourceWidthCurr,
    });
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Splits a stacked layout element at the specified time
   */
  commandSplitLayoutElements(
    elementId: string,
    splitTime: number,
  ): [SplitResultElements, SplitResultElements] | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new SplitLayoutElementsCommand(this, element, {
      splitTime,
    });
    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Deletes a stacked layout element, along with its children and associated data
   */
  commandDeleteLayoutElements(elementId: string): boolean {
    const element = this.getElementById(elementId);
    if (!element) {
      return false;
    }

    const command = new DeleteLayoutElementsCommand(this, element);
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Trims a stacked layout element at the specified time
   */
  commandTrimLayoutElements(
    elementId: string,
    trimType: 'start' | 'end',
    trimTime: number,
  ): boolean {
    const element = this.getElementById(elementId);
    if (!element) {
      return false;
    }

    const command = new TrimLayoutElementsCommand(this, element, {
      trimType,
      trimTime,
    });
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Excises a stacked layout element at the specified time
   */
  commandExciseLayoutElements(
    elementId: string,
    mediaRemovalStart: number,
    mediaRemovalEnd: number,
  ): [ExciseResultElements, ExciseResultElements] | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new ExciseLayoutElementsCommand(this, element, {
      mediaRemovalStart,
      mediaRemovalEnd,
    });
    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Merges two stacked layout elements
   */
  commandMergeLayoutElements(
    prev: Element,
    next: Element,
    track: Track,
  ): boolean {
    const command = new MergeLayoutElementsCommand(this, undefined, {
      prev,
      next,
      track,
    });
    const success = this.executeCommand(command);
    return success;
  }

  /**
   * Switches the stacked layout type
   */
  commandSwitchLayoutType(
    elementId: string,
    layoutType: 'stacked-1-1' | 'stacked-1-3',
    videoSourceWidthPrev?: number,
    videoSourceWidthCurr?: number,
  ): SwitchResultElements | null {
    const element = this.getElementById(elementId);
    if (!element) {
      return null;
    }

    const command = new SwitchLayoutTypeCommand(this, element, {
      layoutType,
      videoSourceWidthPrev,
      videoSourceWidthCurr,
    });
    const success = this.executeCommand(command);
    return success ? command.getResultElements() : null;
  }

  /**
   * Gets a timeline element by ID
   */
  getElementById(elementId: string): Element | undefined {
    return this.timeline.allElements.find((el) => el.id === elementId);
  }

  /**
   * Gets a track by ID
   */
  getTrackById(trackId: string): Track | undefined {
    return this.timeline.tracks.find((track) => track.id === trackId);
  }

  /**
   * Creates a renderer sync observer that automatically syncs changes to the renderer
   * This is an example of how to use the observer pattern for common operations
   */
  createRendererSyncObserver(): EditorObserver {
    return {
      onEditorChange: (editor, changeType) => {
        // We can filter which changes we want to react to
        // For renderer sync, we might want to respond to most changes
        if (changeType === EditorChangeType.TIMELINE_CHANGED) {
          VideoEditorLogger.logStateChange('sample_observer_sync', {
            changeType,
          });
        }
      },
    };
  }

  /**
   * Creates a transcript refresh observer that updates active transcript when relevant
   * Responds to changes in the video elements that affect which parts of the transcript are active
   */
  createTranscriptRefreshObserver(): EditorObserver {
    return {
      onEditorChange: (editor, changeType, payload) => {
        // TODO: Fine grain control over which changes trigger transcript refresh?
        if (changeType === EditorChangeType.PROJECT_LOADED) {
          // When a project is loaded, update the active transcript
          VideoEditorLogger.logTranscriptOperation('refresh_transcript', {
            trigger: 'project_load',
            payload,
          });
          this.updateActiveTranscript();
        } else if (changeType === EditorChangeType.TIMELINE_CHANGED) {
          // General timeline changes might affect active transcript
          VideoEditorLogger.logTranscriptOperation('refresh_transcript', {
            trigger: 'timeline_change',
            source: payload?.source,
            payload,
          });
          this.updateActiveTranscript();
        }
      },
    };
  }

  // Timeline Element Operations with Command Pattern

  /**
   * Moves an element to a new time position
   * Uses command pattern and delegates to TrackManager via MoveElementCommand
   * @param elementId The ID of the element to move
   * @param newStartTime The new start time for the element (in seconds)
   * @param targetTrackId Optional track ID to move the element to (if different from current track)
   * @returns true if the operation was successful, false otherwise
   */
  commandShiftElement(
    elementId: string,
    newStartTime: number,
    targetTrackId?: string,
  ): boolean {
    // Resolve element and track if IDs were provided
    const element = this.getElementById(elementId);
    if (!element) return false;

    let targetTrack = targetTrackId
      ? this.getTrackById(targetTrackId)
      : undefined;

    // Execute the move command with the resolved objects
    return this.executeCommand(
      new ShiftElementCommand(this, element, newStartTime, targetTrack),
    );
  }

  /**
   * Trims the start of an element (adjusts start time and duration)
   * Uses command pattern and delegates to TrackManager via TrimElementStartCommand
   * @param elementId The element or ID of the element to trim
   * @param newStartTime The new start time for the element (in seconds)
   * @returns true if the operation was successful, false otherwise
   */
  commandTrimElementStart(elementId: string, newStartTime: number): boolean {
    // Resolve element if an ID was provided
    const element = this.getElementById(elementId);

    if (!element) return false;

    return this.executeCommand(
      new TrimElementStartCommand(this, element, newStartTime),
    );
  }

  /**
   * Trims the end of an element (adjusts duration)
   * Uses command pattern and delegates to TrackManager via TrimElementEndCommand
   * @param elementId The element or ID of the element to trim
   * @param newEndTime The new end time for the element (in seconds)
   * @returns true if the operation was successful, false otherwise
   */
  commandTrimElementEnd(elementId: string, newEndTime: number): boolean {
    // Snap the time to frame if enabled
    const snappedTime = this.timeline.snapTimeToFrame(newEndTime);

    // Resolve element if an ID was provided
    const element = this.getElementById(elementId);

    if (!element) return false;

    return this.executeCommand(
      new TrimElementEndCommand(this, element, snappedTime),
    );
  }

  /**
   * Splits an element at the specified time
   * @param elementId The ID of the element to split
   * @param splitTime The time (in seconds) where the split should occur (playhead position)
   * @returns An array with the two resulting elements, or null if the operation failed
   */
  commandSplitElement(
    elementId: string,
    splitTime: number,
  ): [Element, Element] | null {
    const element = this.getElementById(elementId);
    if (!element) return null;

    // Validate that split time is within element bounds
    if (
      splitTime <= element.time ||
      splitTime >= element.time + element.durationSeconds
    ) {
      VideoEditorLogger.logTimelineOperation('split_element_error', {
        error: 'Split time must be within element bounds',
        element: element.id,
        elementTime: element.time,
        elementEnd: element.time + element.durationSeconds,
        requestedSplitTime: splitTime,
      });
      return null;
    }

    const command = new SplitElementCommand(this, element, splitTime);
    const success = this.executeCommand(command);

    return success ? command.getResultElements() : null;
  }

  /**
   * Shifts a track up in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  commandShiftTrackLayerUp(trackId: string): boolean {
    return this.executeCommand(new TrackLayerUpCommand(this, trackId));
  }

  /**
   * Shifts a track down in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  commandShiftTrackLayerDown(trackId: string): boolean {
    return this.executeCommand(new TrackLayerDownCommand(this, trackId));
  }

  // TRANSCRIPT METHODS

  /**
   * Loads a transcript into the editor
   * @param transcriptElements The original transcript elements to load
   */
  loadTranscript(
    transcriptElements: TranscriptElement[],
    trackId?: string,
  ): void {
    // This is a special operation that replaces the entire transcript,
    // so we don't use a command for it
    this.transcript = new Transcript(transcriptElements);

    if (!trackId) {
      // If no track ID is provided, use the first video track
      const videoTrack = this.timeline.getVideoTrack();

      if (!videoTrack) {
        throw new Error('No video track available to attach transcript');
      }

      trackId = videoTrack.id;
    }

    // Store the track ID for future reference
    this.setTranscriptTrack(trackId);

    // Notify observers about the transcript loading
    this.notifyObservers(EditorChangeType.TRANSCRIPT_LOADED, {
      transcript: this.transcript,
    });

    // Also notify about active transcript changing since it's derived from the original
    this.updateActiveTranscript();
  }

  // /**
  //  * Removes a segment of transcript by adjusting video elements
  //  * @param wordIds Array of word IDs that represent the segment to remove
  //  * @returns true if successful, false otherwise
  //  */
  // commandRemoveTranscriptSegment(wordIds: string[]): boolean {
  //   const transcriptTrack = this.getTranscriptTrack();
  //   if (!transcriptTrack) {
  //     console.error(
  //       'Cannot remove transcript segment: No transcript track set',
  //     );
  //     return false;
  //   }

  //   return this.executeCommand(
  //     new TranscriptRemoveCommand(this, wordIds, transcriptTrack),
  //   );
  // }

  /**
   * Cut a segment of the transcript
   * @param segments Array of objects containing elementId and initialIndexes for segments to cut
   * @returns true if successful, false otherwise
   */
  commandCutTranscriptSegment(segments: CutTranscriptSegment[]): boolean {
    // Store the cut indexes
    this.cutInitialIndexes = segments.flatMap(
      (segment) => segment.initialIndexes,
    );

    return this.commandRemoveTranscriptSegment(segments);
  }

  /**
   * Remove segments of transcript from one or more elements
   * @param segments Array of objects containing elementId and initialIndexes for segments to remove
   * @returns true if all segments were successfully removed, false otherwise
   */
  commandRemoveTranscriptSegment(
    segments: Array<{ elementId: string; initialIndexes: Array<number> }>,
  ): boolean {
    return this.executeInBatch(() => {
      let success = true;

      // Process each segment in the array
      for (const [idx, segment] of segments.entries()) {
        const { elementId, initialIndexes } = segment;

        if (!elementId || !initialIndexes || initialIndexes.length === 0) {
          VideoEditorLogger.logTranscriptOperation(
            'remove_transcript_segment_error',
            {
              error: 'invalid_segment',
              elementId,
              initialIndexes,
            },
          );
          success = false;
          continue;
        }

        // Find min and max indices for this segment
        const startInitialIndex = Math.min(...initialIndexes);
        const endInitialIndex = Math.max(...initialIndexes);

        // Create and execute the command for this segment
        const result = this.executeCommand(
          new TranscriptSimpleRemoveCommand(
            this,
            elementId,
            startInitialIndex,
            endInitialIndex,
            idx > 0, // do not trim extra before first segment
            idx < segments.length - 1, // do not trim extra after last segment
          ),
        );

        // Track overall success - only successful if all segments are removed
        success = success && result;
      }

      return success;
    });
  }

  /**
   * Gets the currently cut transcript words' initial indexes
   * @returns Array of initial indexes that were cut, or empty array if none available
   */
  getCutTranscriptInitialIndexes(): number[] {
    return [...this.cutInitialIndexes];
  }

  // /**
  //  * Paste previously cut transcript words at a specific position
  //  * @param pasteAtWordId The word ID where to paste the cut segment
  //  * @returns true if successful, false otherwise
  //  */
  // commandPasteTranscriptWords(pasteAtWordId: string): boolean {
  //   if (this.cutTranscriptWordIds.length === 0) {
  //     console.warn('No cut words available to paste');
  //     return false;
  //   }

  //   const transcriptTrack = this.getTranscriptTrack();
  //   if (!transcriptTrack) {
  //     console.error('Cannot paste transcript words: No transcript track set');
  //     return false;
  //   }

  //   return this.executeCommand(
  //     new TranscriptInsertCommand(
  //       this,
  //       this.cutTranscriptWordIds,
  //       pasteAtWordId,
  //       transcriptTrack,
  //     ),
  //   );
  // }

  /**
   * Paste previously cut transcript words at a specific time position
   * @param time The time position to paste at
   * @param startInitialIndex The start initial index of the segment to paste
   * @param endInitialIndex The end initial index of the segment to paste
   * @returns true if successful, false otherwise
   */
  commandPasteTranscriptWords(
    pasteSegments: PasteTranscriptSegment[],
  ): boolean {
    if (this.cutInitialIndexes.length === 0) {
      VideoEditorLogger.logTranscriptOperation('paste_transcript_words_error', {
        error: 'no_cut_words',
      });
      return false;
    }

    return this.executeInBatch(() => {
      let success = true;

      for (const segment of pasteSegments) {
        const { intoTime, startInitialIndex, endInitialIndex } = segment;
        if (startInitialIndex === undefined || endInitialIndex === undefined) {
          VideoEditorLogger.logTranscriptOperation(
            'paste_transcript_words_error',
            {
              error: 'invalid_segment',
              segment,
            },
          );
          success = false;
          continue;
        }

        // Create and execute the command for this segment
        const result = new TranscriptSimpleInsertCommand(
          this,
          intoTime,
          startInitialIndex,
          endInitialIndex,
        ).execute();

        // Track overall success - only successful if all segments are inserted
        success = success && result;
      }

      return success;
    });
  }

  /**
   * Restore a previously removed transcript element, simply.
   */
  commandTranscriptSimpleRestore(
    prevElementId: string | null,
    nextElementId: string | null,
    startInitialIndex: number,
    endInitialIndex: number,
  ): boolean {
    if (!this.transcript) {
      VideoEditorLogger.logTranscriptOperation('simple_restore_error', {
        error: 'no_transcript',
      });
      return false;
    }

    return this.executeCommand(
      new TranscriptSimpleRestoreCommand(
        this,
        prevElementId,
        nextElementId,
        startInitialIndex,
        endInitialIndex,
      ),
    );
  }

  /**
   * Gets the original, immutable transcript
   * @returns The original transcript elements, or undefined if no transcript is loaded
   */
  getOriginalTranscript(): ReadonlyArray<TranscriptElement> | undefined {
    if (!this.transcript) return undefined;
    return this.transcript.getOriginalTranscription();
  }

  /** WIP - Inferred Transcript
  //  * Gets the inferred full transcript based on timeline elements
  //  * Infers which parts of the transcript should be present based on gaps between elements
  //  * @returns Array of transcript elements with status property indicating if each element
  //  * is included in the timeline (status='included') or was removed (status='removed')
  //  */
  // getInferredFullTranscript(): InferredTranscriptElement[] {
  //   console.log('getInferredFullTranscript called');

  //   if (!this.transcript) {
  //     console.error('getInferredFullTranscript: this.transcript is undefined', {
  //       hasTranscriptProperty: this.hasOwnProperty('transcript'),
  //       typeOfTranscript: typeof this.transcript,
  //     });
  //     return [];
  //   }

  //   // If we have no original transcript, there's nothing to infer from
  //   const originalTranscription = this.transcript.getOriginalTranscription();
  //   if (!originalTranscription || originalTranscription.length === 0) {
  //     console.error(
  //       'getInferredFullTranscript: No original transcription available',
  //     );
  //     return [];
  //   }

  //   console.log('getInferredFullTranscript: this.transcript exists', {
  //     timeline: {
  //       tracksCount: this.timeline.tracks.length,
  //       elementsCount: this.timeline.allElements.length,
  //       hasVideoTracks: this.timeline.tracks.some((t) => t.type === 'video'),
  //     },
  //     originalTranscriptLength: originalTranscription.length,
  //   });

  //   const result = this.transcript.inferFullTranscript(this.timeline);

  //   // Check if result is valid and has content
  //   if (!result || result.length === 0) {
  //     console.error(
  //       'getInferredFullTranscript: inferFullTranscript returned empty result',
  //     );

  //     // As a fallback, return the original transcript with all elements marked as included
  //     return originalTranscription.map((element) => ({
  //       ...element,
  //       status: 'included',
  //     })) as InferredTranscriptElement[];
  //   }

  //   console.log(`getInferredFullTranscript: result length = ${result.length}`);
  //   return result;
  // }

  /**
   * Gets the active transcript based on video elements from the transcript track
   * @returns The active transcript elements with modifications applied and timeline timestamps
   */
  getActiveTranscript(): TranscriptElement[] | undefined {
    if (!this.transcript) return undefined;

    // Get video elements from the designated transcript track if set, otherwise from all tracks
    let videoElements: VideoElement[] = [];

    if (this.transcriptTrackId) {
      const transcriptTrack = this.getTranscriptTrack();
      if (transcriptTrack) {
        videoElements = transcriptTrack.elements
          .filter((e) => e.type === 'video')
          .map((e) => e as VideoElement);
        console.log(
          `Using ${videoElements.length} video elements from transcript track ${this.transcriptTrackId}`,
        );
      }
    }

    // Initialize each video element with the original transcription data
    // This ensures that transcript segments can be correctly derived
    // TODO: Find a better way to ensure transcrt track elements always ahve original transcription
    //        new TranscriptTrack object?
    const originalTranscription = this.transcript.getOriginalTranscription();
    videoElements.forEach((ve) => {
      ve.setOriginalTranscription([...originalTranscription]);
    });

    // Get active transcript using the Transcript class's existing implementation
    const activeTranscript = this.transcript.getActiveTranscript(videoElements);

    if (!activeTranscript) return undefined;

    // Add timeline timestamps to active transcript elements
    return activeTranscript;
  }

  /**
   * Updates the active transcript and notifies observers
   * This should be called whenever the timeline changes in a way that could affect
   * the active transcript (e.g., video elements added, modified, or removed)
   */
  public updateActiveTranscript(): void {
    if (!this.transcript) return;

    VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
      action: 'begin',
    });

    // Get and log video elements from the transcript track
    const transcriptTrack = this.getTranscriptTrack();

    if (transcriptTrack) {
      const videoElements = transcriptTrack.elements
        .filter((e) => e.type === 'video')
        .map((e) => ({
          id: e.id,
          time: e.time,
          duration: e.duration,
          trim_start: (e as VideoElement).trim_start,
        }));
      VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
        action: 'track_video_elements',
        trackId: transcriptTrack.id,
        videoElementCount: videoElements.length,
        videoElements,
      });
    } else {
      VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
        action: 'warning',
        message:
          'No transcript track set. Active transcript may be incomplete.',
      });

      // Log all video elements for debugging
      const allVideoElements = this.timeline.allElements
        .filter((e) => e.type === 'video')
        .map((e) => ({
          id: e.id,
          time: e.time,
          duration: e.duration,
          trim_start: (e as VideoElement).trim_start,
        }));
      VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
        action: 'all_video_elements',
        videoElementCount: allVideoElements.length,
        videoElements: allVideoElements,
      });
    }

    // Get active transcript based on current settings
    const activeTranscript = this.getActiveTranscript();
    VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
      action: 'result',
      activeTranscriptElementCount: activeTranscript?.length || 0,
    });

    // Also get the inferred transcript to see if it's working
    // const inferredTranscript = this.getInferredFullTranscript();
    // VideoEditorLogger.logTranscriptOperation('update_active_transcript', {
    //   action: 'inferred_result',
    //   inferredTranscriptElementCount: inferredTranscript?.length || 0
    // });

    // Force a re-render by creating a new array instance even if the content is the same
    const forcedNewArray = activeTranscript ? [...activeTranscript] : [];

    // Notify observers about the active transcript changing
    this.notifyObservers(EditorChangeType.ACTIVE_TRANSCRIPT_CHANGED, {
      activeTranscript: forcedNewArray,
      // inferredTranscript: inferredTranscript ? [...inferredTranscript] : [],
    });
  }

  /**
   * Gets a transcript element by ID from the original transcript
   * @param id ID of the element to find (can be word_index or initial_index)
   * @returns The transcript element, or undefined if not found or no transcript is loaded
   */
  getTranscriptElementById(id: string): TranscriptElement | undefined {
    if (!this.transcript) return undefined;

    // First try to find by word_index (for transcript elements)
    const elementByWordIndex = this.transcript.getElementByWordIndex(id);
    if (elementByWordIndex) {
      return elementByWordIndex;
    }

    // If not found, fall back to initial_index lookup
    return this.transcript.getElementById(id);
  }

  /**
   * Begins a batch of operations, deferring renderer synchronization and observer notifications.
   * Maintains backward compatibility with the depth counter approach while using a simpler boolean flag.
   */
  beginBatch(): void {
    this.batchDepth++;
    this.inBatch = true;

    // Log batch operation
    VideoEditorLogger.logBatch('begin', this.batchDepth);
  }

  /**
   * Ends a batch of operations. Only syncs to renderer and processes notifications at the outermost batch level.
   * Maintains backward compatibility with the depth counter approach while using a simpler boolean flag.
   * @returns true if this was the outermost batch that completed, false for nested batches
   */
  endBatch(): boolean {
    if (this.batchDepth <= 0 || !this.inBatch) {
      throw new Error('No active batch to end');
    }

    // Decrement the batch depth counter
    const oldDepth = this.batchDepth;
    this.batchDepth--;

    // Log batch operation
    VideoEditorLogger.logBatch('end', this.batchDepth, {
      previousDepth: oldDepth,
    });

    // Only take action at the outermost level
    if (this.batchDepth === 0) {
      // Reset the inBatch flag at the outermost level
      this.inBatch = false;

      // Performance timing for syncing
      const syncStart = performance.now();
      let syncDuration = 0;

      // Only sync if changes were made during the batch
      if (this.dirty) {
        this.rendererSynchronizer.syncToRenderer();
        this.dirty = false;
        syncDuration = performance.now() - syncStart;

        // Log performance of sync operation
        VideoEditorLogger.logPerformance('renderer_sync', syncDuration);
      }

      // Add a batch completion notification to the queue
      this.observerManager.queueNotification(
        EditorChangeType.TIMELINE_CHANGED,
        {
          source: 'batch_complete',
          changes: 'multiple_operations',
        },
      );

      // Process all queued notifications after batch is complete and sync is done
      const notifStart = performance.now();
      this.observerManager.processQueuedNotifications();
      const notifDuration = performance.now() - notifStart;

      // Log performance of notification processing
      VideoEditorLogger.logPerformance('process_notifications', notifDuration);

      return true;
    }

    return false;
  }

  /**
   * Alias for endBatch to maintain compatibility
   */
  commitBatch(): boolean {
    return this.endBatch();
  }

  /**
   * Aborts the current batch operation, restoring the batch state without syncing
   */
  abortBatch(): void {
    if (this.batchDepth <= 0 || !this.inBatch) {
      throw new Error('No active batch to abort');
    }

    const oldDepth = this.batchDepth;

    // Log batch abort
    VideoEditorLogger.logBatch('abort', this.batchDepth, {
      reason: 'explicit_abort',
      previousDepth: oldDepth,
    });

    // Reset to no batching state
    this.batchDepth = 0;
    this.inBatch = false;
  }

  /**
   * Returns whether the editor is currently syncing to the renderer
   * Used by ObserverManager to determine if notifications should be queued
   */
  isSyncingToRenderer(): boolean {
    return this._isSyncingToRenderer;
  }

  /**
   * Sets a track as the designated transcript track
   * @param trackId The ID of the track to set as transcript track
   * @returns boolean indicating success
   */
  setTranscriptTrack(trackId: string): boolean {
    const track = this.getTrackById(trackId);
    if (!track) {
      VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
        error: 'track_not_found',
        trackId,
      });
      return false;
    }

    if (track.type !== 'video') {
      VideoEditorLogger.logTranscriptOperation('set_transcript_track_error', {
        error: 'not_video_track',
        trackId,
        actualType: track.type,
      });
      return false;
    }

    this.transcriptTrackId = trackId;
    VideoEditorLogger.logTranscriptOperation('set_transcript_track', {
      trackId,
    });
    return true;
  }

  /**
   * Gets the current transcript track
   * @returns The transcript track or undefined if not set
   */
  getTranscriptTrack(): Track | undefined {
    if (!this.transcriptTrackId) return undefined;
    return this.getTrackById(this.transcriptTrackId);
  }

  public getElementAtTime(
    trackId: string,
    time: number,
  ): Record<string, any> | null {
    // Find the element in the source that matches the given time
    const track = this.timeline.getTrack(trackId);
    if (!track) {
      VideoEditorLogger.logTimelineOperation('get_element_at_time_error', {
        error: 'track_not_found',
        trackId,
        time,
      });
      throw new Error(`Track with ID ${trackId} does not exist`);
    }

    const elements = track.elements;
    const element = elements.find((el) => {
      return el.time <= time && time < el.end_time;
    });

    if (!element) {
      VideoEditorLogger.logTimelineOperation('get_element_at_time', {
        status: 'not_found',
        trackId,
        time,
        elementsInTrack: elements.length,
      });
      return null;
    }

    VideoEditorLogger.logTimelineOperation('get_element_at_time', {
      status: 'found',
      trackId,
      time,
      elementId: element.id,
      elementType: element.type,
    });
    return element || null;
  }

  public passExtraElementData(extraElementData: Video['extraElementData']) {
    this.trackManager.extraElementDataRaw = extraElementData;
  }
}
